import React, { useState, useEffect } from "react";
import { PageHeader } from "components/ui";
import {
  Grid,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import StandardsSelector from "components/customizedStandardsComponent/GriStandardsSelector";
import UploadEvidenceComponent from "components/customizedStandardsComponent/UploadEvidenceComponent";
import DownloadReport from "components/customizedStandardsComponent/DownloadReport";
import DownloadPostReviewGapForm from "components/customizedStandardsComponent/DownloadPostReviewGapForm";
import UploadPostReviewGapForm from "components/customizedStandardsComponent/UploadPostReviewGapForm";
import DownloadPostReviewReports from "components/customizedStandardsComponent/DownloadPostReviewReports";
import {
  COMPANY_IDENTIFICATION_OPERATIONAL,
  ACTIVITY_SPECIFICATION_OPERATIONAL,
  REQUIREMENT_SELECTION_INT_EXT_OPERATIONAL,
} from "data/standards/griStandards";
import NavigationButtons from "helper/navigation/NavigationButtons";
import ReusableFormComponent from "components/customizedStandardsComponent/EsgFormComponent";
import { OPERATIONAL_COMPLIANCE_ESG_COMPLIANCE_FORM } from "data/forms/griFormSchema";
import {
  monitoringReportingFrequency,
  typesOfReportCompaniesOperational,
  dashboardDataProcessing,
} from "data/reports/relevantReports";
import ReportsReviewDialog from "components/customizedStandardsComponent/ReportsReviewDialog";
import UploadEvidenceReviewReports from "components/customizedStandardsComponent/UploadEvidenceReviewReports";
import { useNavigate } from "react-router-dom";

const OperationalComplianceReporting = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [prevStep, setPrevStep] = useState(0);
  const [reportData, setReportData] = useState({
    selectedStandards: [],
    companyInfo: {},
  });
  const [showReviewPopup, setShowReviewPopup] = useState(false);

  const steps = [
    "Select Sector/Industry Specific Standards",
    "Select Applicable Standard Framework",
    "Select Annexure 5",
    "Reporting Requirements",
    "Select Types of Reports",
    "Fill Sector Specific ESG Compliance Form",
    "Upload Evidence",
    "Download Reports and Assessments",
    "Upload Evidence and Review Reports (2)",
    "Download Post Review Gap Form",
    "Upload Post Review Gap Form",
    "Download Post Review Reports",
  ];

  useEffect(() => {
    if (activeStep === 8 && prevStep === 7) {
      setShowReviewPopup(true);
    }
  }, [activeStep, prevStep]);

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setPrevStep(activeStep);
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleSkipToDashboard = () => {
    setShowReviewPopup(false);
    navigate("/"); // Navigate to dashboard
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setPrevStep(activeStep);
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    }
  };

  const handleSaveStandards = (selectedStandards) => {
    setReportData((prev) => ({
      ...prev,
      selectedStandards,
    }));
    console.log("Standards saved:", selectedStandards);
  };

  const handleClosePopup = () => {
    setShowReviewPopup(false);
  };

  const handleSkipToStep5 = () => {
    setPrevStep(activeStep);
    setActiveStep(6); // Adjusted to skip to "Upload Evidence" step
    setShowReviewPopup(false);
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <StandardsSelector
            key="Company-Identification-Operational"
            standards={COMPANY_IDENTIFICATION_OPERATIONAL}
            onNext={handleNext}
            showReportCountdownButton={true}
            onSave={handleSaveStandards}
            title="Select from the options below"
            nextButtonText="Next"
            initialSelectedStandards={reportData.selectedStandards.map(
              (s) => s.id
            )}
          />
        );
      case 1:
        return (
          <StandardsSelector
            key="Activity-Specification-Operational"
            standards={ACTIVITY_SPECIFICATION_OPERATIONAL}
            onNext={handleNext}
            onSave={handleSaveStandards}
            title="Select from the options below"
            nextButtonText="Next"
            initialSelectedStandards={reportData.selectedStandards.map(
              (s) => s.id
            )}
          />
        );
      case 2:
        return (
          <StandardsSelector
            key="Requirement-Selection-Int-Ext-Operational"
            standards={REQUIREMENT_SELECTION_INT_EXT_OPERATIONAL}
            onNext={handleNext}
            onSave={handleSaveStandards}
            title="Please select your compliance and reporting requirements."
            nextButtonText="Next"
            initialSelectedStandards={reportData.selectedStandards.map(
              (s) => s.id
            )}
          />
        );
      case 3:
        return (
          <DownloadReport
            key="reporting-requirements-step"
            onPrevious={handleBack}
            onNext={handleNext}
            initialItems={monitoringReportingFrequency}
            title="Set Monitoring and Reporting Frequency:"
            ReportDownloadButtonShow={false}
          />
        );
      case 4:
        return (
          <DownloadReport
            key="reporting-companies-operational-step"
            onPrevious={handleBack}
            onNext={handleNext}
            initialItems={typesOfReportCompaniesOperational}
            title="What types of reports would you like to generate through ESG Vision?"
            ReportDownloadButtonShow={false}
          />
        );
      case 5:
        return (
          <ReusableFormComponent
            key="esg-compliance-form-step"
            {...OPERATIONAL_COMPLIANCE_ESG_COMPLIANCE_FORM}
            onNext={handleNext}
            onPrevious={handleBack}
            showPreviousButton={true}
          />
        );
      case 6:
        return (
          <UploadEvidenceComponent
            key="upload-evidence-step"
            onPrevious={handleBack}
            onNext={handleNext}
          />
        );
      case 7:
        return (
          <DownloadReport
            key="download-reports-assessments-step"
            onPrevious={handleBack}
            onNext={handleNext}
            initialItems={dashboardDataProcessing}
            showReportCountdownButton={true}

          />
        );
      case 8:
        return (
          <UploadEvidenceReviewReports
            onPrevious={handleBack}
            onNext={handleNext}
          />
        );
      case 9:
        return (
          <DownloadPostReviewGapForm
            key="download-post-review-gap-form-step"
            onPrevious={handleBack}
            onNext={handleNext}
          />
        );
      case 10:
        return (
          <UploadPostReviewGapForm
            key="upload-post-review-gap-form-step"
            onPrevious={handleBack}
            onNext={handleNext}
          />
        );
      case 11:
        return (
          <DownloadPostReviewReports
            key="download-post-review-reports-step"
            onPrevious={handleBack}
            standardName="Operational Compliance"
            showReportCountdownButton={true}
          />
        );
      default:
        return "Unknown step";
    }
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <PageHeader title="AI-Powered ESG Reporting across Global, National, Sectoral, and Industry-Specific Standards" />
          </Box>
          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <NavigationButtons
                imageKey="dashboardImg4_3"
                title={"Operational Compliance ESG Reporting"}
                activeStep={activeStep}
                onBack={handleBack}
              />
            </Box>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          {getStepContent(activeStep)}
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 4,
              mb: 2,
            }}
          >
            {steps.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: index <= activeStep ? "#02462E" : "#e0e0e0",
                  mx: 1,
                }}
              />
            ))}
          </Box>
        </Grid>
      </Grid>

      <ReportsReviewDialog
        open={showReviewPopup}
        onClose={handleClosePopup}
        onSkip={handleSkipToDashboard}
      />
    </>
  );
};

export default OperationalComplianceReporting;
